import React, { useState } from "react";
import {
  Star,
  MessageSquare,
  CalendarDays,
  User,
  ChevronDown,
  CheckCircle,
  Clock,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { formatDate } from "@/utils/formatters";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import SkillScoreDisplay from "./SkillScoreDisplay";

interface AssessmentCardProps {
  assessment: {
    id: number | string;
    task_title: string;
    rating: number;
    partner_name: string;
    student_name?: string;
    assessed_at: string;
    feedback: string;
    skill_scores?: {
      communication: number;
      technical: number;
      punctuality: number;
      teamwork: number;
    };
    kpi_metrics?: {
      tasks_completed: number;
      on_time: boolean;
      quality_score: number;
    };
    assessed_by?: string;
  };
  variant?: "compact" | "full";
  className?: string;
}

function Stars({ rating, size }: { rating: number; size: number }) {
  return (
    <span className="flex" role="img" aria-label={`Rated ${rating.toFixed(1)} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={1.5}
          className={i < Math.round(rating) ? "fill-warning text-warning" : "text-border"}
          aria-hidden
        />
      ))}
    </span>
  );
}

export default function AssessmentCard({
  assessment,
  variant = "full",
  className,
}: AssessmentCardProps) {
  const [expanded, setExpanded] = useState(false);

  const getRatingTone = (rating: number) => {
    if (rating >= 4.5) return "text-success";
    if (rating >= 3) return "text-warning";
    return "text-destructive";
  };

  const ratingTone = getRatingTone(assessment.rating);
  const initials = (assessment.partner_name || "P").charAt(0).toUpperCase();

  const toggle = (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={expanded ? "Collapse details" : "Expand details"}
      aria-expanded={expanded}
      className="text-muted-foreground hover:text-foreground"
      onClick={(e) => {
        e.stopPropagation();
        setExpanded(!expanded);
      }}
    >
      <ChevronDown className={cn("transition-transform", expanded && "rotate-180")} />
    </Button>
  );

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "flex cursor-pointer items-center justify-between gap-3 border-b border-border px-4 py-3 transition-colors hover:bg-muted/60",
          className,
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-muted text-caption font-medium text-foreground"
            aria-hidden
          >
            {initials}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-medium text-foreground">
              {assessment.task_title}
            </p>
            <p className="truncate text-[11px] text-muted-foreground">
              {assessment.partner_name} · {formatDate(assessment.assessed_at)}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Stars rating={assessment.rating} size={14} />
          {toggle}
        </div>
      </div>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "cursor-pointer overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-hairline-strong",
        className,
      )}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="flex size-10 shrink-0 items-center justify-center rounded-sm border border-border bg-muted text-sm font-medium text-foreground"
              aria-hidden
            >
              {initials}
            </span>
            <div className="min-w-0">
              <h3 className="text-[15px] font-medium text-foreground">{assessment.task_title}</h3>
              <p className="text-caption text-muted-foreground">{assessment.partner_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Stars rating={assessment.rating} size={16} />
            <span className={cn("font-mono text-sm font-medium tabular-nums", ratingTone)}>
              {assessment.rating.toFixed(1)}
            </span>
            {toggle}
          </div>
        </div>

        {/* Meta row */}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-caption text-muted-foreground sm:ml-[52px]">
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="size-3.5" aria-hidden />
            {formatDate(assessment.assessed_at)}
          </span>
          {assessment.assessed_by && (
            <span className="inline-flex items-center gap-1">
              <User className="size-3.5" aria-hidden />
              {assessment.assessed_by}
            </span>
          )}
        </div>
      </div>

      {/* Feedback snippet */}
      <div className="flex items-start gap-2 px-4 py-3">
        <MessageSquare className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <p className={cn("text-[13px] leading-relaxed text-body", !expanded && "line-clamp-2")}>
          {assessment.feedback}
        </p>
      </div>

      {/* Expanded details */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 border-t border-border p-4">
              {assessment.skill_scores && (
                <SkillScoreDisplay scores={assessment.skill_scores} title="Skill Breakdown" compact />
              )}
              {assessment.kpi_metrics && (
                <div className="rounded-md border border-border bg-background p-3">
                  <p className="eyebrow mb-2 text-muted-foreground">KPI Performance</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-caption text-body">
                    <span className="inline-flex items-center gap-1.5">
                      <CheckCircle className="size-3.5 text-success" aria-hidden />
                      {assessment.kpi_metrics.tasks_completed} tasks completed
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      {assessment.kpi_metrics.on_time ? (
                        <CheckCircle className="size-3.5 text-success" aria-hidden />
                      ) : (
                        <Clock className="size-3.5 text-destructive" aria-hidden />
                      )}
                      {assessment.kpi_metrics.on_time ? "On time" : "Late"}
                    </span>
                    <Badge variant="success">
                      Quality: {assessment.kpi_metrics.quality_score.toFixed(1)}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}
